import { Image, Page, Text, View } from '@react-pdf/renderer'
import { DatePicker, Form, Input, Switch } from 'antd'
import GeckoLogo from 'features/reporting/pdfGeneration/components/shared/GeckoLogo'
import { GradientStripe } from 'features/reporting/pdfGeneration/components/shared/GradientStripe'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { formatDate } from 'features/reporting/pdfGeneration/utils/formatters'
import { PdfProps, ReportSection } from 'features/reporting/types'
import {
  CoverPageData,
  MainInfo,
} from 'pages/reporting/reports/raut/rautReportData'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import { TYPE_TO_METHOD_TITLE } from 'pages/reporting/shared/constants'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { GeckoInspectionType } from 'types'

// =============================================================================
// STYLED COMPONENTS FOR COVER PAGE FORM
// =============================================================================

const CoverFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const FormSectionCard = styled.div`
  background: ${({ theme }) =>
    theme.forecast.cardBackground || theme.forecast.background1};
  border: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
  border-radius: 0.5rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 0.0625rem 0.25rem
    ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.06)'};
`

const FormSectionHeader = styled.h3`
  margin: 0 0 1rem 0;
  padding: 0 0 0.75rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  letter-spacing: 0.025em;
  text-transform: uppercase;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
`

const FormFieldGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 1rem;

  .ant-form-item {
    margin-bottom: 0;
  }

  & + & {
    margin-top: 1rem;
  }
`

const ScanTypeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.625rem 0.875rem;
  background: ${({ theme }) =>
    theme.forecast.cardGroupBackground || theme.forecast.background2};
  border-radius: 0.375rem;
  border: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
`

const ScanTypeItem = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.activeText};
  padding: 0.25rem 0;
`

const requiredCoverPageFields = [
  'customerName',
  'asset',
  'location',
  'scanType',
  'geckoJobId',
  'startDate',
  'endDate',
  'inspectionSupervisorName',
  'inspectionSupervisorQuals',
  'reviewerName',
  'reviewerQuals',
  'revisionNumber',
]

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData, uniqueScanTypesString } = useRautReportOps()
  const signoff = useMemo(() => formData.signoff, [formData.signoff])
  const data = useMemo(
    () => ({ ...formData.coverPageData, ...formData.mainInfo }),
    [formData]
  )

  const oneDayInspection = useMemo(
    () =>
      formData.coverPageData.startDate
        ? formData.coverPageData.startDate.isSame(
            formData.coverPageData.endDate
          )
        : false,
    [formData.coverPageData.endDate, formData.coverPageData.startDate]
  )

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  return (
    <Page size='A4' style={PDFStyles.page} id='cover' wrap={false}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'cover', pageNumber })
          return ''
        }}
      />

      <View style={PDFStyles.content}>
        {/* Header with Gecko logo and gradient stripe */}
        <View style={PDFStyles.coverHeader}>
          <GeckoLogo />
          <View style={PDFStyles.coverHeaderContent}>
            <Text style={PDFStyles.coverHeaderText}>
              {`${isPrelim ? 'Preliminary' : 'Automated'} Ultrasonic Inspection Report`}
            </Text>
            <GradientStripe />
          </View>
        </View>

        {/* Customer Section */}
        <View style={PDFStyles.coverPageSectionContainer}>
          <View style={PDFStyles.coverPageSectionTitle}>
            <View style={PDFStyles.coverPageDash} />
            <Text style={PDFStyles.coverPageTitleText}>Customer</Text>
          </View>
          <View style={PDFStyles.coverPageSectionContent}>
            <Text style={PDFStyles.coverPageValueTextLarge}>
              {data.customerName}
            </Text>
            <Text style={PDFStyles.coverPageValueTextLarge}>
              {data.location}
            </Text>
          </View>
        </View>

        {/* Scan Methods Section */}
        <View style={PDFStyles.coverPageSectionContainer}>
          <View style={PDFStyles.coverPageSectionTitle}>
            <View style={PDFStyles.coverPageShortDash} />
            <Text style={PDFStyles.coverPageTitleText}>Method</Text>
          </View>
          <View style={PDFStyles.coverPageSectionContent}>
            <Text style={PDFStyles.coverPageValueText}>
              {uniqueScanTypesString}
            </Text>
          </View>
        </View>

        {/* Asset Section */}
        <View style={PDFStyles.coverPageSectionContainer}>
          <View style={PDFStyles.coverPageSectionTitle}>
            <View style={PDFStyles.coverPageDash} />
            <Text style={PDFStyles.coverPageBoldTitleText}>Asset</Text>
          </View>
          <View style={PDFStyles.coverPageSectionContent}>
            <Text style={PDFStyles.coverPageValueText}>{data.asset}</Text>
          </View>
        </View>

        {/* Job Number Section */}
        <View style={PDFStyles.coverPageSectionContainer}>
          <View style={PDFStyles.coverPageSectionTitle}>
            <View style={PDFStyles.coverPageShortDash} />
            <Text style={PDFStyles.coverPageTitleText}>Gecko Job ID</Text>
          </View>
          <View style={PDFStyles.coverPageSectionContent}>
            <Text style={PDFStyles.coverPageValueText}>{data.geckoJobId}</Text>
          </View>
        </View>

        {/* Inspection Dates Section */}
        <View style={PDFStyles.coverPageSectionContainer}>
          <View style={PDFStyles.coverPageSectionTitle}>
            <View style={PDFStyles.coverPageDash} />
            <Text style={PDFStyles.coverPageTitleText}>
              {oneDayInspection ? 'Date of Inspection' : 'Dates of Inspection'}
            </Text>
          </View>
          <View style={PDFStyles.coverPageSectionContent}>
            <Text style={PDFStyles.coverPageValueText}>
              {data.startDate && data.endDate
                ? oneDayInspection
                  ? `${formatDate(data.startDate)}`
                  : `${formatDate(data.startDate)} - ${formatDate(data.endDate)}`
                : 'Not specified'}
            </Text>
          </View>
        </View>

        {/* Signature Section */}
        <View style={PDFStyles.signatureSection}>
          {/* Field Technician Row */}
          <View style={PDFStyles.contributorRow}>
            <Text style={PDFStyles.contributorHeaderText}>
              Field Technician
            </Text>
            <Text style={PDFStyles.contributorHeaderText}>Certification</Text>
            <Text style={PDFStyles.contributorHeaderText}>Date</Text>
            <Text style={PDFStyles.contributorHeaderText}>Signature</Text>
          </View>
          <View style={PDFStyles.contributorBox}>
            <Text style={PDFStyles.contributorText}>
              {signoff?.supervisorSignoff?.name || ''}
            </Text>
            <Text style={PDFStyles.contributorText}>
              {signoff?.supervisorSignoff?.qualifications || ''}
            </Text>
            <Text style={PDFStyles.contributorText}>
              {signoff?.supervisorSignoff
                ? formatDate(signoff.supervisorSignoff.timestamp)
                : ''}
            </Text>
            {signoff?.supervisorSignoff ? (
              <Image
                src={signoff.supervisorSignoff.signature}
                style={PDFStyles.signatureImage}
              />
            ) : (
              <Text style={PDFStyles.contributorText}></Text>
            )}
          </View>

          {/* Reviewer Row */}
          <View style={PDFStyles.contributorRow}>
            <Text style={PDFStyles.contributorHeaderText}>Reviewed By</Text>
            <Text style={PDFStyles.contributorHeaderText}>Certification</Text>
            <Text style={PDFStyles.contributorHeaderText}>Date</Text>
            <Text style={PDFStyles.contributorHeaderText}>Signature</Text>
          </View>
          <View style={PDFStyles.contributorBox}>
            <Text style={PDFStyles.contributorText}>
              {signoff?.reviewerSignoff?.name || ''}
            </Text>
            <Text style={PDFStyles.contributorText}>
              {signoff?.reviewerSignoff?.qualifications || ''}
            </Text>
            <Text style={PDFStyles.contributorText}>
              {signoff?.reviewerSignoff
                ? formatDate(signoff.reviewerSignoff.timestamp)
                : ''}
            </Text>
            {signoff?.reviewerSignoff ? (
              <Image
                src={signoff.reviewerSignoff.signature}
                style={PDFStyles.signatureImage}
              />
            ) : (
              <Text style={PDFStyles.contributorText}></Text>
            )}
          </View>
        </View>
      </View>

      <PageFooter
        jobNumber={formData.coverPageData.geckoJobId}
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<CoverPageData & MainInfo>()
  const {
    // Form Data
    formData,
    handleCoverPageFinish,
    handleMainInfoFinish,
    saveCoverPageSilent,
    saveMainInfoSilent,
    registerPageSaveHandler,
    uniqueScanTypes,
  } = useRautReportOps()

  const initialValues = React.useMemo(
    () => ({
      ...formData.coverPageData,
      ...formData.mainInfo,
    }),
    [formData.coverPageData, formData.mainInfo]
  )

  const hasPopulatedRef = useRef(false)

  const personnelInfo = React.useMemo(
    () =>
      formData.signoff
        ? {
            supervisorName: formData.signoff.supervisorSignoff?.name || '',
            supervisorQuals:
              formData.signoff.supervisorSignoff?.qualifications || '',
            reviewerName: formData.signoff.reviewerSignoff?.name || '',
            reviewerQuals:
              formData.signoff.reviewerSignoff?.qualifications || '',
          }
        : undefined,
    [formData.signoff]
  )

  const handleFormSubmit = useCallback(
    async (values: CoverPageData & MainInfo) => {
      await handleCoverPageFinish(values)
      await handleMainInfoFinish(values)
    },
    [handleCoverPageFinish, handleMainInfoFinish]
  )

  const saveBeforeNavigation = useCallback(async () => {
    const values = form.getFieldsValue(true) as CoverPageData & MainInfo
    await saveCoverPageSilent(values)
    await saveMainInfoSilent(values)
  }, [form, saveCoverPageSilent, saveMainInfoSilent])

  useEffect(() => {
    registerPageSaveHandler('cover', saveBeforeNavigation)

    return () => {
      registerPageSaveHandler('cover', null)
    }
  }, [registerPageSaveHandler, saveBeforeNavigation])

  useEffect(() => {
    if (hasPopulatedRef.current) return
    const valuesToSet = Object.fromEntries(
      Object.entries(initialValues).filter(([, v]) => v !== undefined)
    )
    if (Object.keys(valuesToSet).length > 0) {
      form.setFieldsValue(valuesToSet)
      hasPopulatedRef.current = true
    }
  }, [form, initialValues])

  return (
    <StyledForm<CoverPageData & MainInfo>
      form={form}
      name='coverPageForm'
      onFinish={handleFormSubmit}
      layout='vertical'
      initialValues={initialValues}
      disabled={formData.lockState?.isLocked}>
      <CoverFormContainer>
        {/* Customer & Asset Information Section */}
        <FormSectionCard>
          <FormSectionHeader>Customer Information</FormSectionHeader>
          <FormFieldGroup>
            <Form.Item name='customerName' label='Customer Name'>
              <Input />
            </Form.Item>

            <Form.Item name='asset' label='Asset'>
              <Input />
            </Form.Item>

            <Form.Item name='location' label='Location'>
              <Input />
            </Form.Item>
          </FormFieldGroup>
        </FormSectionCard>

        {/* Inspection Details Section */}
        <FormSectionCard>
          <FormSectionHeader>Inspection Details</FormSectionHeader>
          <FormFieldGroup>
            <Form.Item label='Scan Type'>
              <ScanTypeList>
                {uniqueScanTypes.map((scanType) => (
                  <ScanTypeItem key={scanType}>
                    {TYPE_TO_METHOD_TITLE[scanType as GeckoInspectionType]}
                  </ScanTypeItem>
                ))}
              </ScanTypeList>
            </Form.Item>

            <Form.Item name='geckoJobId' label='Gecko Job ID'>
              <Input />
            </Form.Item>
          </FormFieldGroup>

          <FormFieldGroup>
            <Form.Item name='startDate' label='Start Date'>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name='endDate' label='End Date'>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </FormFieldGroup>
        </FormSectionCard>

        {/* Personnel Information Section */}
        {personnelInfo && (
          <FormSectionCard>
            <FormSectionHeader>Personnel Information</FormSectionHeader>
            <FormFieldGroup>
              <Form.Item label='Inspection Supervisor Name'>
                <Input value={personnelInfo.supervisorName} disabled />
              </Form.Item>

              <Form.Item label='Inspection Supervisor Qualifications'>
                <Input value={personnelInfo.supervisorQuals} disabled />
              </Form.Item>
            </FormFieldGroup>

            <FormFieldGroup>
              <Form.Item label='Reviewer Name'>
                <Input value={personnelInfo.reviewerName} disabled />
              </Form.Item>

              <Form.Item label='Reviewer Qualifications'>
                <Input value={personnelInfo.reviewerQuals} disabled />
              </Form.Item>
            </FormFieldGroup>
          </FormSectionCard>
        )}

        {/* Report Settings Section */}
        <FormSectionCard>
          <FormSectionHeader>Report Settings</FormSectionHeader>
          <FormFieldGroup>
            {formData.revisionMetadata && (
              <Form.Item label='Revision Number'>
                <Input
                  value={formData.revisionMetadata.revisionNumber}
                  disabled
                />
              </Form.Item>
            )}

            <Form.Item
              name='scanIndicationMode'
              label='Scan Indication Mode'
              valuePropName='checked'>
              <Switch />
            </Form.Item>
          </FormFieldGroup>
        </FormSectionCard>
      </CoverFormContainer>
    </StyledForm>
  )
}

export const Cover: ReportSection = {
  RenderReact,
  required: true,
  RenderPDF,
  id: 'cover',
  description: 'Basic report information and metadata',
  title: 'Cover Page',
  requiredFields: requiredCoverPageFields,
}
